import { useState, useEffect } from 'react';
import api from '../../config/axios';
import { MdAdd, MdEdit, MdDelete, MdClose } from 'react-icons/md';
import InputMask from 'react-input-mask';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

const AgentSettings = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    agent_status: true,
    full_name: '',
    username: '',
    password_hash: '',
    agent_type: 'Agent',
    department_id: '',
    agent_email: '',
    agent_gsm: '',
    company_id: null
  });
  const [departments, setDepartments] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [popup, setPopup] = useState({
    show: false,
    title: '',
    message: '',
    type: 'confirm',
    onConfirm: null,
    showCancel: true
  });

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      setFormData(prev => ({
        ...prev,
        company_id: userData.companyId
      }));
      fetchDepartments(userData.companyId);
      fetchAgents(userData.companyId);
    }
  }, []);

  const canEditAgentType = (() => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      const user = JSON.parse(userStr);
      // Admin veya Owner her şeyi yapabilir; Agent yapamaz
      return user.agent_type === 'Admin' || user.agent_type === 'Owner';
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    if (showForm) {
      const timer = setTimeout(() => {
        const input = document.querySelector('input[name="full_name"]');
        if (input) input.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showForm]);

  useEffect(() => {
    // URL'de edit parametresi varsa, ilgili agent'ı bulup düzenleme moduna geç
    const params = new URLSearchParams(window.location.search);
    const editId = params.get('edit');
    if (editId && agents.length > 0) {
      // UUID veya ID ile eşleşen agent'ı bul
      const agentToEdit = agents.find(a => a.company_agent_uuid === editId || String(a.id) === editId);
      if (agentToEdit) {
        setFormData({
          agent_status: agentToEdit.agent_status === 1,
          full_name: agentToEdit.full_name,
          username: agentToEdit.username,
          password_hash: '',
          agent_type: agentToEdit.agent_type,
          department_id: agentToEdit.department_id,
          agent_email: agentToEdit.agent_email,
          agent_gsm: agentToEdit.agent_gsm,
          company_id: agentToEdit.company_id
        });
        setIsEditing(true);
        setEditingId(agentToEdit.id);
        setShowForm(true);
      }
    }
    // Sadece ilk yüklemede çalışsın diye agents değişince tetiklenir
  }, [agents]);

  const fetchAgents = async (companyId) => {
    try {
      const response = await api.get(`/agents/company/${companyId}`);
      if (response.data.success) {
        setAgents(response.data.agents);
      }
    } catch (error) {
      console.error('Agentlar yüklenirken hata:', error);
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/admin');
      } else {
        alert('Agentlar yüklenirken bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (agent, agentName) => {
    const agentIdentifier = agent.company_agent_uuid || agent.id; // UUID varsa UUID, yoksa ID kullan
    
    showPopup({
      title: t('agentSettings.deleteAgent'),
      message: t('agentSettings.deleteConfirm', { name: agentName }),
      type: 'confirm',
      onConfirm: async () => {
        try {
          const response = await api.delete(`/agents/${agentIdentifier}`);
          if (response.data.success) {
            setAgents(agents.filter(a => a.id !== agent.id));
            showPopup({
              title: t('common.success'),
              message: t('agentSettings.agentDeleted', { name: agentName }),
              type: 'success',
              showCancel: false,
              onConfirm: closePopup
            });
          }
        } catch (error) {
          showPopup({
            title: t('common.error'),
            message: error.response?.data?.message || t('agentSettings.deleteError'),
            type: 'error',
            showCancel: false,
            onConfirm: closePopup
          });
        }
      }
    });
  };

  const fetchDepartments = async (companyId) => {
    try {
      const response = await api.get(`/departments/${companyId}`);
      setDepartments(response.data.departments);
    } catch (error) {
      console.error('Departmanlar yüklenirken hata:', error);
    }
  };

  const departmentOptions = [
    'Profesyonel Hizmetler',
    'Teknik Destek',
    'Müşteri Hizmetleri',
    'Satış',
    'Yönetim'
  ];

  const agentTypeOptions = [
    'Admin',
    'Agent'
  ];

  const handleEdit = (agent) => {
    setFormData({
      agent_status: agent.agent_status === 1,
      full_name: agent.full_name,
      username: agent.username,
      password_hash: '', // Şifre alanını boş bırak
      agent_type: agent.agent_type,
      department_id: agent.department_id,
      agent_email: agent.agent_email,
      agent_gsm: agent.agent_gsm,
      company_id: agent.company_id
    });
    setIsEditing(true);
    setEditingId(agent.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_id) {
      showPopup({
        title: t('common.error'),
        message: t('agentSettings.companyNotFound'),
        type: 'error',
        showCancel: false
      });
      return;
    }

    try {
      let response;
      const submitData = { ...formData };
      
      if (isEditing && !submitData.password_hash) {
        delete submitData.password_hash;
      }

      if (isEditing) {
        response = await api.put(`/agents/${editingId}`, submitData);
        
        if (submitData.password_hash && response.data.success) {
          showPopup({
            title: t('agentSettings.passwordChanged'),
            message: t('agentSettings.passwordChangedMessage'),
            type: 'success',
            showCancel: false,
            onConfirm: handleLogout
          });
          return;
        }
      } else {
        response = await api.post('/agents', submitData);
      }

      if (response.data.success) {
        // Form'u hemen kapat
        setShowForm(false);
        setIsEditing(false);
        setEditingId(null);
        
        // Agent listesini güncelle
        const userData = JSON.parse(localStorage.getItem('user'));
        fetchAgents(userData.companyId);
        
        // Form verilerini sıfırla
        setFormData(prev => ({
          agent_status: true,
          full_name: '',
          username: '',
          password_hash: '',
          agent_type: 'Agent',
          department_id: '',
          agent_email: '',
          agent_gsm: '',
          company_id: prev.company_id
        }));
        
        // Popup'ı göster
        showPopup({
          title: t('common.success'),
          message: isEditing 
            ? t('agentSettings.agentUpdated')
            : t('agentSettings.agentCreated'),
          type: 'success',
          showCancel: false,
          onConfirm: closePopup
        });
      }
    } catch (error) {
      showPopup({
        title: t('common.error'),
        message: error.response?.data?.message || t('agentSettings.operationError'),
        type: 'error',
        showCancel: false,
        onConfirm: closePopup
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/admin');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              name === 'department_id' ? Number(value) : // department_id'yi number'a çeviriyoruz
              value
    }));
  };

  // Popup yönetimi için fonksiyonlar
  const showPopup = (options) => {
    setPopup({
      show: true,
      title: options.title || 'Onay',
      message: options.message,
      type: options.type || 'confirm',
      onConfirm: options.onConfirm,
      showCancel: options.showCancel ?? true
    });
  };

  const closePopup = () => {
    setPopup(prev => ({ ...prev, show: false }));
  };

  // Sayfa boyutu seçenekleri
  const pageSizeOptions = [10, 20, 50, 100, 500];

  // Sayfa boyutunu değiştirme fonksiyonu
  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(Number(newSize));
    setCurrentPage(1); // Sayfa boyutu değişince ilk sayfaya dön
  };

  // Sayfalama hesaplamaları güncellendi
  const indexOfLastAgent = currentPage * itemsPerPage;
  const indexOfFirstAgent = indexOfLastAgent - itemsPerPage;
  const currentAgents = agents.slice(indexOfFirstAgent, indexOfLastAgent);
  const totalPages = Math.ceil(agents.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="h-full overflow-auto">
      <div className="flex justify-between items-center gap-3 mb-6">
        <h1 className="text-xl sm:text-2xl font-medium text-gray-800 dark:text-white">{t('settings.agentSettings')}</h1>
        {canEditAgentType && (
        <button
          onClick={() => {
            // Form verilerini temizle
            setFormData(prev => ({
              agent_status: true,
              full_name: '',
              username: '',
              password_hash: '',
              agent_type: 'Agent',
              department_id: '',
              agent_email: '',
              agent_gsm: '',
              company_id: prev.company_id
            }));
            setIsEditing(false);
            setEditingId(null);
            setShowForm(true);
          }}
          className="flex items-center justify-center gap-2 px-3 py-2.5 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] transition-colors w-11 h-10 sm:w-auto sm:h-auto sm:px-6 sm:py-3 -ml-2.5 sm:ml-0"
        >
          <MdAdd size={20} className="sm:hidden" />
          <MdAdd size={24} className="hidden sm:block" />
          <span className="hidden sm:inline">{t('agentSettings.addNewAgent')}</span>
        </button>
        )}
      </div>

      {/* Total Count */}
      <div className="mb-4">
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {t('agentSettings.totalAgents', { count: agents.length })}
        </span>
      </div>

      {/* Agent Listesi */}
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${totalPages <= 1 ? 'rounded-b-lg' : ''}`}>
        {/* Sayfa boyutu seçici */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-[#565c66] bg-gray-50 dark:bg-gray-700 flex justify-between items-center rounded-tl-lg rounded-tr-lg">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('common.show')}</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('table.entries')}</span>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full min-w-[600px] table-auto">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
              <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('agentSettings.status')}</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('agentSettings.fullName')}</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('agentSettings.username')}</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('agentSettings.department')}</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('agentSettings.type')}</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('agentSettings.email')}</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('agentSettings.gsm')}</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('agentSettings.lastLogin')}</th>
                <th className="px-4 py-2 text-right text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('table.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {currentAgents.map((agent, index) => {
                const isLast = index === currentAgents.length - 1 && totalPages <= 1;
                return (
                  <tr 
                    key={agent.id} 
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200"
                    onClick={() => navigate(`/settings/agent/${agent.company_agent_uuid || agent.id}`)}
                  >
                    {/* İlk hücre: Eğer son satırsa sol alt köşe yuvarlatılır */}
                    <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white ${isLast ? 'rounded-bl-lg' : ''}`}>
                      <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                        agent.agent_status 
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' 
                          : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                      }`}>
                        {agent.agent_status ? t('agentSettings.active') : t('agentSettings.inactive')}
                      </span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{agent.full_name}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{agent.username}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{agent.department_name}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{agent.agent_type}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{agent.agent_email}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{agent.agent_gsm}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {agent.last_login ? new Date(agent.last_login).toLocaleString() : '-'}
                    </td>
                    {/* Son hücre: Eğer son satırsa sağ alt köşe yuvarlatılır */}
                    <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right font-medium ${isLast ? 'rounded-br-lg' : ''}`}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/settings/agent/${agent.company_agent_uuid || agent.id}?edit=true`);
                        }}
                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                      >
                        <MdEdit size={18} />
                      </button>
                                             {agent.agent_type === 'Owner' ? (
                         <button
                           disabled
                           className="text-gray-400 cursor-not-allowed"
                           title={t('agentSettings.ownerCannotBeDeleted')}
                         >
                           <MdDelete size={18} />
                         </button>
                       ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(agent, agent.full_name);
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <MdDelete size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden">
          <div className="space-y-4 p-4">
            {currentAgents.map((agent, index) => (
              <div 
                key={agent.id}
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                onClick={() => navigate(`/settings/agent/${agent.company_agent_uuid || agent.id}`)}
              >
                {/* Status & Full Name */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentSettings.status')}</div>
                    <div>
                      <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                        agent.agent_status 
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' 
                          : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                      }`}>
                        {agent.agent_status ? t('agentSettings.active') : t('agentSettings.inactive')}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentSettings.fullName')}</div>
                    <div className="text-sm text-gray-900 dark:text-white font-medium">{agent.full_name}</div>
                  </div>
                </div>

                {/* Username & Department */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentSettings.username')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{agent.username}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentSettings.department')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{agent.department_name}</div>
                  </div>
                </div>

                {/* Type & Email */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentSettings.type')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{agent.agent_type}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentSettings.email')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{agent.agent_email}</div>
                  </div>
                </div>

                {/* GSM & Last Login */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentSettings.gsm')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{agent.agent_gsm}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentSettings.lastLogin')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">
                      {agent.last_login ? new Date(agent.last_login).toLocaleString() : '-'}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('table.actions')}</div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/settings/agent/${agent.company_agent_uuid || agent.id}?edit=true`);
                        }}
                        className="text-indigo-600 hover:text-indigo-900 p-1"
                      >
                        <MdEdit size={16} />
                      </button>
                      {agent.agent_type === 'Owner' ? (
                        <button
                          disabled
                          className="text-gray-400 cursor-not-allowed p-1"
                          title={t('agentSettings.ownerCannotBeDeleted')}
                        >
                          <MdDelete size={16} />
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(agent, agent.full_name);
                          }}
                          className="text-red-600 hover:text-red-900 p-1"
                        >
                          <MdDelete size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination - Alt padding artırıldı */}
        {totalPages > 1 && (
          <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 rounded-b-lg">
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('table.showing')}{' '}
                  <span className="font-medium">{indexOfFirstAgent + 1}</span>
                  {' '}-{' '}
                  <span className="font-medium">
                    {Math.min(indexOfLastAgent, agents.length)}
                  </span>{' '}
                  {t('table.of')}{' '}
                  <span className="font-medium">{agents.length}</span>{' '}
                  {t('agentSettings.agents')}
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('table.previous')}
                  </button>
                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index + 1}
                      onClick={() => handlePageChange(index + 1)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                        currentPage === index + 1
                          ? 'bg-[#272c39] text-white'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('table.next')}
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}


      </div>

      {/* Popup - z-index'i form modal'ından daha yüksek yapıldı */}
      {popup.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
          <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header */}
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">{popup.title}</h3>
            </div>
            
            <div className="p-8 bg-white dark:bg-gray-800">
              <div className="text-center">
                <p className="text-base text-gray-600 dark:text-gray-400 mb-6">
                  {popup.message}
                </p>
                <div className="flex justify-center gap-4">
                  {popup.showCancel && (
                    <button
                      onClick={closePopup}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                    >
                      {t('common.cancel')}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (popup.onConfirm) {
                        popup.onConfirm();
                      } else {
                        closePopup();
                      }
                    }}
                    className={`px-4 py-2 text-white rounded-md ${
                      popup.type === 'error' 
                        ? 'bg-red-600 hover:bg-red-700'
                        : popup.type === 'success'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-[#272c39] hover:bg-[#1e2230]'
                    }`}
                  >
                    {popup.type === 'confirm' ? t('common.confirm') : t('common.ok')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Agent Ekleme/Düzenleme Modal Formu - daha düşük z-index ve ortalanmış */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center p-2 sm:p-4">
          <div className="relative mx-auto w-full max-w-4xl shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header */}
            <div className="bg-[#272c39] text-white px-3 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
              <h3 className="text-sm sm:text-lg font-medium">
                {isEditing ? t('agentSettings.editAgent') : t('agentSettings.addNewAgent')}
              </h3>
              <button
                onClick={() => {
                  setShowForm(false);
                  setIsEditing(false);
                  setEditingId(null);
                  setFormData({
                    agent_status: true,
                    full_name: '',
                    username: '',
                    password_hash: '',
                    agent_type: 'Agent',
                    department_id: '',
                    agent_email: '',
                    agent_gsm: '',
                    company_id: formData.company_id
                  });
                }}
                className="text-white hover:text-gray-200"
              >
                <MdClose size={20} />
              </button>
            </div>
            
            <div className="p-3 sm:p-8 bg-white dark:bg-gray-800">
              <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {/* Status */}
              <div className="col-span-1 sm:col-span-2 flex items-center">
                <input
                  type="checkbox"
                  id="agent_status"
                  name="agent_status"
                  checked={formData.agent_status}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300"
                />
                <label htmlFor="agent_status" className="ml-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                  {t('agentSettings.active')}
                </label>
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('agentSettings.fullName')}
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                />
              </div>

              {/* Username */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('agentSettings.username')}
                </label>
                <input
                  type="email"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  autoComplete="new-username"
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('agentSettings.password')}
                </label>
                <input
                  type="password"
                  name="password_hash"
                  value={formData.password_hash}
                  onChange={handleChange}
                  autoComplete="new-password"
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required={!isEditing} // Düzenleme modunda zorunlu değil
                />
              </div>

              {/* Agent Type */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('agentSettings.agentType')}
                </label>
                <select
                  name="agent_type"
                  value={formData.agent_type}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                  disabled={!canEditAgentType}
                >
                  {agentTypeOptions.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Department */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('agentSettings.department')}
                </label>
                <select
                  name="department_id"
                  value={formData.department_id}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                >
                  <option value="">{t('agentSettings.selectDepartment')}</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.department_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('agentSettings.email')}
                </label>
                <input
                  type="email"
                  name="agent_email"
                  value={formData.agent_email}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                />
              </div>

              {/* GSM */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('agentSettings.gsm')}
                </label>
                <InputMask
                  mask="(999) 999-9999"
                  type="tel"
                  name="agent_gsm"
                  value={formData.agent_gsm}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                  placeholder="(555) 555-5555"
                />
              </div>

              {/* Buttons */}
              <div className="col-span-1 sm:col-span-2 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-3 sm:mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setIsEditing(false);
                    setEditingId(null);
                  }}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 text-sm sm:text-base"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] text-sm sm:text-base"
                >
                  {isEditing ? t('agentSettings.updateAgent') : t('agentSettings.addAgent')}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentSettings; 